import axios, { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { IItemBranchesConfig, IItemBranchesConfigurationValidation, IItemSizeBranchesConfig } from '../types/itemBranchesConfig';
import { defaultItemBranchesConfigurationValidation } from '../constants/itemBranchesConfiguration';
import client from 'src/services/client';
import { IPaginationLight } from 'src/types/main';
import { getCancelToken } from 'src/utils/helpers';
import logger from '~/services/logger';
import { defaultPaginationLight } from '~/constants/main';
import { toastSuccess } from '~/utils/toasts';
import { localeStore } from '~/mobx/localesStore';
import { roundPrice } from '~/utils/formatter';
import { addItemInStore } from '~/helpers';

interface IItemBranchesConfigWithPagination {
  page: number;
  config: IItemBranchesConfig[] | null;
}

export class ItemBranchesConfigStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;
  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable public page: number = 1;

  @observable public itemId: number = 0;

  @observable public loading: boolean = false;
  @observable public isSize: boolean = false;
  @observable public isHasData: boolean = false;
  @observable public isEdited: boolean = false;

  @observable public config: IItemBranchesConfig[] | null = null;
  @observable public tempConfigWithPagination: IItemBranchesConfigWithPagination[] | null = null;
  @observable public pagination: IPaginationLight = defaultPaginationLight;

  @observable validation: IItemBranchesConfigurationValidation = defaultItemBranchesConfigurationValidation;

  @action public fetchData = (): void => {
    this.get();
    addItemInStore(this.itemId);
  };

  @computed get allConfigs(): IItemBranchesConfig[] | null {
    if (this.tempConfigWithPagination?.length) {
      if (this.config?.length) {
        const indexOfCurrentPage = this.tempConfigWithPagination.findIndex((tempConfig) => tempConfig.page === this.page);
        if (indexOfCurrentPage > -1) {
          this.tempConfigWithPagination[indexOfCurrentPage].config = this.config;
        } else {
          this.tempConfigWithPagination.push({ page: this.page, config: this.config });
        }
      }
      return this.tempConfigWithPagination.flatMap((tempConfig) => tempConfig.config) as IItemBranchesConfig[] | null;
    } else {
      return this.config;
    }
  }

  @action public get = async (): Promise<void> => {
    if (this.itemId === 0) return;

    try {
      const cancelId = 'getItemBranchesConfig';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.actionBeforeRequest();

      const response = await client.get(`/v3/items/${this.itemId}/branches/configuration`, {
        params: { page: this.page },
        headers: { 'Accept-Language': localeStore.language },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      if (this.tempConfigWithPagination?.length) {
        const currentPageIndex = this.tempConfigWithPagination.findIndex((tempConfig) => tempConfig.page === this.pagination.current_page);

        if (currentPageIndex > -1) {
          this.tempConfigWithPagination[currentPageIndex].config = this.config;
        } else {
          this.tempConfigWithPagination.push({ page: this.pagination.current_page, config: this.config });
        }
        const requestedPageIndex = this.tempConfigWithPagination.findIndex((tempConfig) => tempConfig.page === this.page);
        if (requestedPageIndex > -1) {
          this.config = this.tempConfigWithPagination[requestedPageIndex].config;
        } else {
          this.config = response.data?.config;
        }
      } else {
        this.tempConfigWithPagination = this.page > 1 ? [{ page: this.pagination.current_page, config: this.config }] : [];
        this.config = response.data?.config;
      }

      this.pagination = response.data?.pagination ?? defaultPaginationLight;

      this.actionAfterRequest();
    } catch (error: any) {
      if (axios.isCancel(error)) return;

      this.actionBeforeRequest();
      logger.error({ error, place: 'ItemBranchesConfigStore get' });
    }
  };

  @action public validate = (): void => {
    this.fixPrices();
    this.validation = defaultItemBranchesConfigurationValidation;

    if (this.isSize) {
      this.validateWithSize();
      return;
    }

    this.validateWithoutSize();
  };

  @action public update = async (): Promise<void> => {
    if (this.itemId === 0 || !this.allConfigs) return;

    try {
      this.loading = true;
      await client.put(`/v3/items/${this.itemId}/branches/configuration`, { ...this.allConfigs });
      this.loading = false;
      this.isEdited = false;

      toastSuccess({ message: localeStore.t('toast_item_changes_saved') });
    } catch (error: any) {
      this.loading = false;

      logger.error({ error, place: 'ItemBranchesConfigStore update', showModal: true });
    }
  };

  @action public unMount = (): void => {
    this.page = 1;

    this.itemId = 0;

    this.loading = false;
    this.isSize = false;
    this.isHasData = false;
    this.isEdited = false;

    this.config = null;
    this.tempConfigWithPagination = null;
    this.pagination = defaultPaginationLight;

    this.validation = defaultItemBranchesConfigurationValidation;
  };

  @action public changeHandler = (branchId: number, part: Partial<IItemBranchesConfig>): void => {
    if (!this.config) return;

    this.config = this.config.map((item) => {
      if (item.branch_id === branchId) {
        this.isEdited = true;

        return { ...item, ...part };
      }

      return item;
    });
  };

  @action public changeSizeHandler = (branchId: number, sizeId: number, part: Partial<IItemSizeBranchesConfig>): void => {
    if (!this.config) return;

    this.config = this.config.map((item) => {
      if (item && item.branch_id === branchId) {
        const branch_item_sizes = item.branch_item_sizes
          ? item.branch_item_sizes.map((size) => {
              if (size.item_size_id === sizeId) {
                this.isEdited = true;

                return { ...size, ...part };
              }

              return size;
            })
          : null;

        return { ...item, branch_item_sizes };
      }

      return item;
    });
  };

  @action private fixPrices(): void {
    if (!this.config) return;

    this.config = this.config.map((item) => {
      const branch_price = Number(roundPrice(item.branch_price));
      const branch_item_sizes = item.branch_item_sizes
        ? item.branch_item_sizes.map((size) => {
            const branch_item_size_price = Number(roundPrice(size.branch_item_size_price));
            const item_size_price = Number(roundPrice(size.item_size_price));
            return { ...size, branch_item_size_price, item_size_price };
          })
        : null;

      return { ...item, branch_price, branch_item_sizes };
    });
  }

  @action private validateWithSize(): void {
    if (!this.config) {
      return;
    }

    const isSizePriceZero = this.config.some((item) => item.branch_item_sizes?.some((size) => size.branch_item_size_price === 0));
    const isOutOfStock = this.config.some((item) => Boolean(item.branch_out_of_stock) !== Boolean(item.item_out_of_stock));
    const isSizeAvailable = this.config.some((item) => Boolean(item.branch_available) !== Boolean(item.item_available));

    if (isSizePriceZero) {
      this.validation.valid = false;
      this.validation.price = false;
    }

    if (isOutOfStock) {
      this.validation.valid = false;
      this.validation.out_of_stock = false;
    }

    if (isSizeAvailable) {
      this.validation.valid = false;
      this.validation.available = false;
    }
  }

  @action private validateWithoutSize(): void {
    if (!this.config) {
      return;
    }

    const isPriseZero = this.config.some((item) => item.branch_price === 0);
    const isAvailable = this.config.some((item) => Boolean(item.branch_available) !== Boolean(item.item_available));
    const isOutOfStock = this.config.some((item) => Boolean(item.branch_out_of_stock) !== Boolean(item.item_out_of_stock));

    if (isPriseZero) {
      this.validation.valid = false;
      this.validation.price = false;
    }

    if (isOutOfStock) {
      this.validation.valid = false;
      this.validation.out_of_stock = false;
    }

    if (isAvailable) {
      this.validation.valid = false;
      this.validation.available = false;
    }
  }

  @action private actionBeforeRequest = (): void => {
    this.loading = true;
    this.isHasData = false;
    this.isSize = false;
  };

  @action private actionAfterRequest = (): void => {
    this.loading = false;
    this.isHasData = this.loading === false && this.config !== null;
    this.isSize = this.config?.[0].item_is_sizes ?? false;
    this.isEdited = false;
  };
}

const itemBranchesConfigStore = new ItemBranchesConfigStore();

export default itemBranchesConfigStore;
